import { useMemo } from 'react';
import { AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { PropertyListItem } from './property-list-item';
import { createPropertyFormDefaults } from './property-editor';
import { BaseSchema, BaseSchemaSchema, Field, SchemaFieldType } from '../../schemas/entity';
import { useListItems } from '../../hooks/use-items';
import { CreateField } from './create-field';

interface SchemaBuilderProps {
  schema: BaseSchema;
  onChange: (schema: BaseSchema) => void;
  entityId: string;
  hasSubEntities: boolean;
}

export function SchemaBuilder({ schema, onChange, entityId, hasSubEntities }: SchemaBuilderProps) {
  const { data: items } = useListItems(entityId);

  const hasItems = (items?.length ?? 0) > 0;
  const fieldNames = Object.keys(schema.fields || {});

  const handleSaveExistingField = (oldName: string) => (name: string, field: Field) => {
    const newSchema = { ...schema };
    newSchema.fields = { ...schema.fields };

    if (oldName !== name) {
      delete newSchema.fields[oldName];
    }

    // If this field is being set as primary, remove primary from other fields
    if (field.primary) {
      Object.keys(newSchema.fields).forEach((key) => {
        if (key !== name) {
          newSchema.fields[key] = { ...newSchema.fields[key], primary: false };
        }
      });
    }

    newSchema.fields[name] = { ...field, order: field.order ?? 0 };

    onChange(newSchema);
  };

  const handleDeleteField = (name: string) => {
    const newSchema = { ...schema };
    newSchema.fields = { ...schema.fields };
    delete newSchema.fields[name];

    onChange(newSchema);
  };

  const handleMoveField = (name: string, direction: 'up' | 'down') => {
    const sortedFields = Object.entries(schema.fields)
      .map(([key, field]) => ({
        name: key,
        field: { ...field, order: field.order ?? 0 }
      }))
      .sort((a, b) => {
        const orderDiff = a.field.order - b.field.order;
        if (orderDiff !== 0) return orderDiff;
        return a.name.localeCompare(b.name);
      });

    const currentIndex = sortedFields.findIndex((item) => item.name === name);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= sortedFields.length) return;

    const newSchema = { ...schema };
    newSchema.fields = { ...schema.fields };

    const currentItem = sortedFields[currentIndex];
    const targetItem = sortedFields[targetIndex];

    newSchema.fields[currentItem.name] = {
      ...currentItem.field,
      order: targetIndex
    };
    newSchema.fields[targetItem.name] = {
      ...targetItem.field,
      order: currentIndex
    };

    onChange(newSchema);
  };

  const existingNames = fieldNames;
  const currentPrimaryField = fieldNames.find((name) => schema.fields[name]?.primary);

  const schemaErrors = useMemo(() => {
    const parsedErrors = BaseSchemaSchema.safeParse(schema).error;

    if (!parsedErrors) {
      return [];
    }

    const errors: string[] = [];

    parsedErrors.issues.forEach((issue) => {
      errors.push(`${issue.path.join(' > ')}: ${issue.message}`);
    });

    return errors;
  }, [schema]);

  const sortedFieldEntries = useMemo(() => {
    return Object.entries(schema.fields)
      .map(([name, field]) => ({
        name,
        field: { ...field, order: field.order ?? 0 }
      }))
      .sort((a, b) => {
        const orderDiff = a.field.order - b.field.order;
        if (orderDiff !== 0) return orderDiff;
        return a.name.localeCompare(b.name);
      });
  }, [schema.fields]);

  const handleCreateField = (data: { name: string; type: SchemaFieldType }) => {
    const newOrder = Object.keys(schema.fields).length;
    const field = createPropertyFormDefaults(data.type, newOrder);

    onChange({
      fields: {
        ...schema.fields,
        [data.name]: field
      }
    });
  };

  return (
    <div className='space-y-4'>
      {!fieldNames.length && (
        <div className='px-4 py-2 text-orange-900 bg-orange-50 rounded-md items-center flex gap-3'>
          <InfoIcon className='size-4.5' />
          <p className='text-sm font-medium'>
            Add fields below to create a structured schema for items in this Entity.
          </p>
        </div>
      )}

      {schemaErrors.length > 0 && (
        <div className='flex flex-col gap-2'>
          {schemaErrors.map((error, index) => (
            <div key={index} className='px-4 py-2 text-red-900 bg-red-50 rounded-md items-center flex gap-3'>
              <AlertTriangleIcon className='size-4.5' />
              <p className='text-sm font-medium'>{error}</p>
            </div>
          ))}
        </div>
      )}

      {/* <div className='flex justify-end'>
        <Button
          variant='outline'
          className='text-sm! font-medium!'
          size='sm'
          onClick={() => setIsAddingNew(!isAddingNew)}
        >
          <Plus className='size-4' />
          Field
          {isAddingNew ? <ChevronUpIcon className='size-4' /> : <ChevronDownIcon className='size-4' />}
        </Button>
      </div> */}
      {/* 
      {isAddingNew && (
        <div className='border border-neutral-200 rounded-lg overflow-hidden'>
          <PropertyEditor
            onSave={handleSaveNewField}
            onDelete={handleDeleteField}
            existingNames={existingNames}
            currentPrimaryField={currentPrimaryField}
            allFields={schema.fields}
            hasItems={false}
            hasSubEntities={false}
          />
        </div>
      )} */}
      <div className='flex flex-col overflow-hidden rounded-lg divide-y divide-neutral-200 border border-neutral-200'>
        {sortedFieldEntries.map(({ name }, index) => (
          <PropertyListItem
            key={name}
            name={name}
            property={schema.fields[name]}
            onSave={handleSaveExistingField(name)}
            onDelete={() => handleDeleteField(name)}
            onMoveUp={() => handleMoveField(name, 'up')}
            onMoveDown={() => handleMoveField(name, 'down')}
            existingNames={existingNames.filter((n) => n !== name)}
            currentPrimaryField={currentPrimaryField}
            allFields={schema.fields}
            isFirst={index === 0}
            isLast={index === sortedFieldEntries.length - 1}
            hasItems={hasItems}
            hasSubEntities={hasSubEntities}
          />
        ))}
        <CreateField onSubmit={handleCreateField} />
      </div>
    </div>
  );
}
